import { defineStore } from 'pinia'
import { ref } from 'vue'
import apiClient from '@/api/client'

export interface Notification {
  id: string
  type: string
  title: string
  message: string
  read_at: string | null
  created_at: string
}

export interface NotificationsMeta {
  total: number
  per_page: number
  current_page: number
  last_page: number
}

export const useNotificationStore = defineStore('notifications', () => {
  // State
  const notifications = ref<Notification[]>([])
  const unreadCount = ref(0)
  const loading = ref(false)
  const meta = ref<NotificationsMeta>({ total: 0, per_page: 20, current_page: 1, last_page: 1 })
  const error = ref<string | null>(null)

  async function fetchNotifications(page = 1, unreadOnly = false): Promise<void> {
    loading.value = true
    error.value = null

    try {
      const params: Record<string, string | number> = { page }
      if (unreadOnly) {
        params.unread_only = 'true'
      }

      const response = await apiClient.get('/api/notifications', { params })
      notifications.value = response.data.data
      unreadCount.value = response.data.unread_count
      meta.value = response.data.meta
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } }
      error.value = axiosErr.response?.data?.message ?? 'Failed to load notifications'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function markRead(id: string): Promise<void> {
    try {
      await apiClient.post(`/api/notifications/${id}/read`)
      // Update local state
      const notification = notifications.value.find((n) => n.id === id)
      if (notification && !notification.read_at) {
        notification.read_at = new Date().toISOString()
        unreadCount.value = Math.max(0, unreadCount.value - 1)
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } }
      error.value = axiosErr.response?.data?.message ?? 'Failed to mark notification as read'
      throw err
    }
  }

  async function markAllRead(): Promise<void> {
    try {
      await apiClient.post('/api/notifications/read-all')
      // Update local state
      notifications.value.forEach((n) => {
        if (!n.read_at) {
          n.read_at = new Date().toISOString()
        }
      })
      unreadCount.value = 0
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } }
      error.value = axiosErr.response?.data?.message ?? 'Failed to mark all notifications as read'
      throw err
    }
  }

  async function fetchUnreadCount(): Promise<void> {
    try {
      const response = await apiClient.get('/api/notifications/unread-count')
      unreadCount.value = response.data.count
    } catch {
      // Silently fail — unread count is supplementary (used for polling)
    }
  }

  return {
    notifications,
    unreadCount,
    loading,
    meta,
    error,
    fetchNotifications,
    markRead,
    markAllRead,
    fetchUnreadCount,
  }
})
